import { useMemo, useState, useEffect } from "react"
import Header from "../../../../layouts/Header"
import { styled, createTheme, ThemeProvider } from "@mui/material/styles"
import {
  Box,
  Grid,
  TextField,
  Typography,
  Paper,
  Button,
  IconButton,
  Tooltip,
  Modal,
  List,
  ListItem,
  FormControlLabel,
  Checkbox,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Container,
} from "@mui/material"
import DeleteIcon from "@mui/icons-material/Delete"
import CloseIcon from "@mui/icons-material/Close"
import BackIcon from "../../../../components/BackIcon"
import { json, useLocation, useNavigate } from "react-router-dom"
import { keyframes } from "@emotion/react"
import Fuse from "fuse.js"
import { useQuery, useMutation } from "@tanstack/react-query"
import fetchwrapper from "../../../../services/interceptors/fetchwrapper"
import CustomBackdrop from "../../../../components/CustomBackdrop"
import CrearIcon from "../../../../assets/iconos/Crear.ico"
import GrabarIcon from "../../../../assets/iconos/Grabar.ico"
import CustomSingleRowSelectionServerSide from "../../../../components/CustomSingleRowSelectionServerSide"
import AddIcon from "@mui/icons-material/Add"
import RemoveIcon from "@mui/icons-material/Remove"
import Swal from "sweetalert2"
import ExpandMoreIcon from "@mui/icons-material/ExpandMore"
import normalFormatDate from "../../../utils/date/DDMMYYYFormatDate"
import normalFormatHour from "../../../utils/date/HHMMSSFormatHour"

const StyledRoot = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  marginTop: theme.spacing(8),
  flexGrow: 1,
  padding: "0 16px",
  height: "100vh",
}))

const theme = createTheme({
  palette: {
    primary: {
      main: "#196C87",
    },
    secondary: {
      main: "#196C87",
    },
  },
})
// ****************************************************************************************
//                           INFORMACION GENERAL COMPONENT
// ***************************************************************************************
const InformacionGeneral = ({ location }) => {
  return (
    <Container>
      <Accordion defaultExpanded sx={{ borderRadius: 2, boxShadow: 3 }}>
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          sx={{
            backgroundColor: (theme) => theme.palette.grey[100],
            borderBottom: "1px solid",
            borderColor: "divider",
          }}
        >
          <Typography variant="h6" fontWeight="bold">
            Información General
          </Typography>
        </AccordionSummary>

        <AccordionDetails>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Typography>
                <strong>Nickname:</strong> {location.state?.nickname}
              </Typography>
              <Typography>
                <strong>Comentario:</strong> {location.state?.comentario}
              </Typography>
              <Typography>
                <strong>ID Cita:</strong> {location.state?.pednumped}
              </Typography>
              <Typography>
                <strong>Fecha Emisión:</strong> {normalFormatDate(location.state?.pedfecemi)}
              </Typography>
              <Typography>
                <strong>Fecha Vencimiento:</strong> {normalFormatDate(location.state?.pedfecven)}
              </Typography>
            </Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>
    </Container>
  )
}

// ****************************************************************************************
//                           CUSTOMMODAL COMPONENT
// ***************************************************************************************
const style = {
  Modal: {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: "98%",
    minHeight: "98%",
    maxHeight: "98%",
    bgcolor: "background.paper",
    border: "2px solid #000",
    boxShadow: 24,
    p: 4,
    overflowY: "auto",
  },

  CloseIcon: {
    cursor: "pointer",
    marginLeft: "95%",
  },
}

const CustomModal = ({ openModal, handleCloseModal, handleAddProduct, location }) => {
  return (
    <div>
      <Modal
        open={openModal}
        onClose={handleCloseModal}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <Box sx={style.Modal}>
          <IconButton style={style.CloseIcon} onClick={() => handleCloseModal()}>
            <CloseIcon />
          </IconButton>

          <div style={{ marginBlock: "20px" }}>
            <InformacionGeneral location={location} />
          </div>

          <CustomSingleRowSelectionServerSide
            endpoint="/VendedorPedidosMostrador/getAllServices"
            errorMsgFilterSearch="Error en cargar datos"
            queryKeyModal="VendedorPedidosMostradorGetAllServices"
            perPage={10}
            onHandleSelectedData={(rowSelected) => {
              handleAddProduct(rowSelected)
              handleCloseModal()
            }}
            columnsTable={[
              {
                accessorKey: "artcodigo",
                header: "Código Artículo",
                size: 200,
                Cell: ({ cell }) => <span>{cell.getValue()}</span>,
              },
              {
                accessorKey: "artdescri",
                header: "Descripción Artículo",
                size: 300,
                Cell: ({ cell }) => <span>{cell.getValue()}</span>,
              },
              {
                accessorKey: "imagen",
                header: "Imagen",
                size: 300,
                Cell: ({ cell }) => {
                  const images = cell.getValue()
                  return (
                    <div>
                      {images && Array.isArray(images) && images.length > 0 ? (
                        <img
                          src={`data:image/jpeg;base64,${images[0]}`}
                          style={{ maxWidth: "100px", height: "100px" }}
                        />
                      ) : (
                        "Imagen no disponible"
                      )}
                    </div>
                  )
                },
              },
            ]}
          />
        </Box>
      </Modal>
    </div>
  )
}

// ****************************************************************************************
//                           CitaPedidosMostrador COMPONENT
// ***************************************************************************************

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
`

const scaleUp = keyframes`
  from { transform: scale(0.95); }
  to { transform: scale(1); }
`

const SearchContainer = styled("div")(({ theme }) => ({
  padding: theme.spacing(4),
  maxWidth: 1200,
  margin: "0 auto",
  [theme.breakpoints.down("sm")]: {
    padding: theme.spacing(2),
  },
}))

const ResultGrid = styled(Grid)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(3),
  width: "100%",
  "& > .MuiGrid-item": {
    width: "100%",
    margin: 0,
    padding: theme.spacing(0.5),
  },
}))

const StyledPaper = styled(Paper)(({ theme }) => ({
  width: "100%",
  transition: "all 0.3s ease",
  padding: theme.spacing(3),
  borderRadius: theme.shape.borderRadius * 2,
  borderLeft: `4px solid ${theme.palette.primary.main}`,
  "&:hover": {
    transform: "translateY(-3px)",
    boxShadow: theme.shadows[4],
    borderLeftWidth: 6,
  },
}))

const SearchMessage = styled("div")(({ theme }) => ({
  textAlign: "center",
  margin: theme.spacing(3),
  color: theme.palette.primary.main,
  animation: `${scaleUp} 0.3s ease`,
}))

const VendedorPedidosMostradorBuscarCita = () => {
  const location = useLocation()
  const navigate = useNavigate()
  // Modal
  const [openModal, setOpenModal] = useState(false)
  const handleOpenModal = () => setOpenModal(true)
  const handleCloseModal = () => setOpenModal(false)
  // Buscador
  const [searchTerm, setSearchTerm] = useState("")
  const [isSearching, setIsSearching] = useState(false)
  // Estado para manejar tanto los productos ya traídos como los nuevos
  const [products, setProducts] = useState([])

  const {
    data: dataProducts = [],
    isLoading: isLoadingProducts,
    isError: isErrorProducts,
    refetch: refetchProducts,
    isFetching: isFetchingProducts,
  } = useGetDataProducts()

  function useGetDataProducts() {
    return useQuery({
      queryKey: ["CitaPedidosMostrador", "Productos"],
      queryFn: async () => {
        const response = await fetchwrapper(`/VendedorPedidosMostrador/getSpecificCitaBuscar`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            pednumped: location.state.pednumped,
          }),
        })
        const result = await response.json()
        return result.data
      },
      keepPreviousData: false,
      retry: 1,
      cacheTime: 0, // Desactiva el caché
      staleTime: 0, // Los datos se consideran obsoletos inmediatamente
      refetchOnMount: "always",
      refetchOnWindowFocus: "always",
      refetchOnReconnect: "always",
    })
  }

  // Cuando se reciben los productos, inicializamos el estado agregando la propiedad isNew: false
  useEffect(() => {
    if (dataProducts.length > 0) {
      setProducts(dataProducts.map((product) => ({ ...product, isNew: false })))
    }
    return () => {
      setProducts([])
    }
  }, [dataProducts])

  // Configuración de Fuse.js para buscar en el array products
  const fuse = useMemo(
    () =>
      new Fuse(products, {
        keys: ["artdescri", "artcodigo"],
        minMatchCharLength: 2,
        threshold: 0.2,
        includeScore: true,
      }),
    [products],
  )

  const results = useMemo(() => {
    if (!searchTerm) return products
    return fuse.search(searchTerm).map((result) => result.item)
  }, [searchTerm, fuse, products])

  // Efecto para simular la búsqueda
  useEffect(() => {
    if (searchTerm) {
      setIsSearching(true)
      const timer = setTimeout(() => setIsSearching(false), 300)
      return () => {
        setIsSearching(false)
        clearTimeout(timer)
      }
    }
  }, [searchTerm])

  // Handler para agregar un nuevo producto
  const handleAddProduct = (product) => {
    const newProduct = {
      ...product,
      isNew: true,
    }
    setProducts((prev) => [newProduct, ...prev])
  }

  // Handler para eliminar un producto nuevo
  const handleDeleteProductFrontend = (artcodigo, index) => {
    setProducts((prev) => prev.filter((product, indexinner) => product.artcodigo !== artcodigo || indexinner !== index))
  }

  // Handler para eliminar un producto que ya estaba guardado en la bd
  const handleDeleteProductBackend = (artcodigo, index) => {
    alert("implementar")
  }

  const { mutateAsync: saveCita, isPending: isSavingCita } = useSaveCita()

  // CREATE hook (post new bancoDePregunta to api)
  function useSaveCita() {
    return useMutation({
      mutationFn: async (cita) => {
        // send api update request here
        const options = {
          method: "POST",
          body: JSON.stringify({
            cabeceraCita: cita.cabeceraCita,
            detalleCita: cita.detalleCita,
          }),
          headers: {
            "Content-Type": "application/json",
            "Access-Control-Allow-Origin": "*",
          },
        }

        const response = await fetchwrapper(`/VendedorPedidosMostrador/saveServicesCita`, options)
        return response
      },
      // eslint-disable-next-line n/handle-callback-err
      onError: (error, variables, context) => {
        // An error happened!
        Swal.fire({
          icon: "error",
          title: "Cita no guardada",
        })
      },
      onSuccess: () => {
        Swal.fire({
          icon: "success",
          title: "Cita guardada con éxito",
        })
        // navigate(-1)
      },
    })
  }

  useEffect(() => {
    refetchProducts()
  }, [dataProducts])

  const handleSaveCita = async () => {
    // Solo mandare a guardar a aquellos servicios que fueron creados en la web
    // (isNew: true) y no traidos inicialmente
    const cabeceraCita = location.state
    const detalleCita = products.filter((product) => product.isNew).reverse()

    if (detalleCita.length > 0) {
      await saveCita({ cabeceraCita, detalleCita })
      refetchProducts()
    } else {
      Swal.fire({
        icon: "warning",
        title: "No se ha registrado ningún servicio nuevo",
      })
    }
  }

  return (
    <ThemeProvider theme={theme}>
      <Header />
      <div className="main main-app p-3 p-lg-4">
        <BackIcon />
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            margin: "0 30px 30px 30px",
            fontSize: "25px",
          }}
        ></div>
        <CustomBackdrop isLoading={isLoadingProducts || isFetchingProducts || isSavingCita} />

        <Box className={StyledRoot}>
          <CustomModal
            openModal={openModal}
            handleCloseModal={handleCloseModal}
            handleAddProduct={handleAddProduct}
            location={location}
          />

          <div style={{ marginTop: "20px" }}>
            <InformacionGeneral location={location} />
          </div>

          <SearchContainer>
            {isErrorProducts ? (
              <Typography color="error" textAlign="center" py={4} sx={{ animation: `${fadeIn} 0.5s ease` }}>
                Error cargando los productos.
                <Button color="primary" onClick={refetchProducts} sx={{ ml: 1 }}>
                  Reintentar
                </Button>
              </Typography>
            ) : (
              <>
                {!isLoadingProducts && products.length === 0 && (
                  <Typography
                    textAlign="center"
                    py={4}
                    color="text.secondary"
                    sx={{ animation: `${fadeIn} 0.5s ease` }}
                  >
                    Esta cita no tiene servcios asociados
                  </Typography>
                )}

                {products.length > 0 && (
                  <>
                    <TextField
                      fullWidth
                      variant="outlined"
                      label="Buscar"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      sx={{
                        marginBottom: 4,
                        "& .MuiOutlinedInput-root": {
                          borderRadius: 50,
                          transition: "all 0.3s ease",
                          "&:hover fieldset": {
                            borderColor: "primary.main",
                          },
                        },
                      }}
                    />

                    {isSearching && <SearchMessage>Buscando...</SearchMessage>}
                    <ResultGrid container>
                      {(results && results.length) > 0
                        ? results.map((block, index) => (
                            <Grid item xs={12} key={`${block.artcodigo}${index}`}>
                              <StyledPaper
                                elevation={3}
                                sx={{
                                  position: "relative",
                                  p: 3,
                                  borderRadius: 2,
                                  backgroundColor: block.isNew ? "#f9f9f9" : "inherit",
                                }}
                              >
                                {/* Botón eliminar posicionado en la esquina superior derecha para productos nuevos creados en el frontend */}
                                {/* {block.isNew && (
                                  <IconButton
                                    color="error"
                                    onClick={() => handleDeleteProductFrontend(block.artcodigo, index)}
                                    sx={{ position: "absolute", top: 8, right: 8 }}
                                  >
                                    <DeleteIcon />
                                  </IconButton>
                                )} */}

                                {/* Botón eliminar posicionado en la esquina superior derecha para productos con permiso de ser eliminados quien mismo los creo en el backend */}
                                {/* Esto solo aplica a servicios que fueron traidos desde el back y solo pueden ser eliminados quien ya los habia creado anteriormente */}
                                {/* {!block.isNew && block?.hasPermissionDelete && (
                                  <IconButton
                                    color="error"
                                    onClick={() => handleDeleteProductBackend(block.artcodigo, index)}
                                    sx={{ position: "absolute", top: 8, right: 8 }}
                                  >
                                    <DeleteIcon />
                                  </IconButton>
                                )} */}

                                <Typography
                                  variant="h6"
                                  sx={{ color: "primary.dark", mb: 1, fontWeight: 600, textAlign: "center" }}
                                >
                                  {block.artdescri} <span>({block.artcodigo})</span>
                                </Typography>

                                <div style={{ textAlign: "center", marginTop: "10px", marginBottom: "18px" }}>
                                  {block?.imagen && block?.imagen?.length > 0 && (
                                    <img
                                      src={`data:image/jpeg;base64,${block.imagen[0]}`}
                                      style={{ maxWidth: "150", height: "150px" }}
                                    />
                                  )}
                                </div>
                                {!block?.isNew && (
                                  <>
                                    <Typography
                                      sx={{
                                        position: "relative",
                                        bottom: 0,
                                        right: 2,
                                        color: "text.secondary",
                                        textAlign: "right",
                                        fontStyle: "italic", // Opcional, puedes darle estilo adicional
                                      }}
                                    >
                                      {`Creado por: ${block.pedusuisys} (${normalFormatHour(block.pedhorisys)})`}
                                      <Typography>{block?.sessecuen && <>Sesión #{`${block.sessecuen}`}</>}</Typography>
                                    </Typography>
                                  </>
                                )}
                              </StyledPaper>
                            </Grid>
                          ))
                        : !isSearching && (
                            <Grid item xs={12}>
                              <Typography
                                sx={{
                                  textAlign: "center",
                                  color: "text.secondary",
                                  py: 4,
                                  animation: `${fadeIn} 0.5s ease`,
                                }}
                              >
                                {searchTerm
                                  ? `No se encontraron resultados para "${searchTerm}"`
                                  : "No hay productos disponibles"}
                              </Typography>
                            </Grid>
                          )}
                    </ResultGrid>
                  </>
                )}
              </>
            )}
          </SearchContainer>
        </Box>
      </div>
    </ThemeProvider>
  )
}

export default VendedorPedidosMostradorBuscarCita
